import React from "react";

/**
 * The lighting inside every dark purple band on the site.
 *
 * It was the same eight-layer stack copied into nine sections, which meant any
 * change to the look had to be made nine times and had drifted already. One
 * component, two tones:
 *
 *   hero — spotlight high in the band, behind a headline near the top
 *   cta  — spotlight centred, behind a closing headline
 *
 * `foot` deepens the bottom edge for bands that hand off to a cream section;
 * bands that hand off to the footer leave it off.
 */
function HeroBackdrop({ tone = "hero", foot = false }) {
  const isHero = tone === "hero";

  return (
    <>
      <div
        className={`absolute inset-0 ${
          isHero
            ? "bg-[linear-gradient(158deg,#42076F_0%,#4C087D_34%,#340659_68%,#220339_100%)]"
            : "bg-[linear-gradient(158deg,#4C087D_0%,#3B0764_46%,#2A0447_78%,#220339_100%)]"
        }`}
        aria-hidden="true"
      />

      {/* Key light, placed behind wherever the headline sits. */}
      <div
        className={`absolute inset-0 ${
          isHero
            ? "bg-[radial-gradient(ellipse_68%_52%_at_50%_16%,rgba(168,85,247,0.42),transparent_72%)]"
            : "bg-[radial-gradient(ellipse_65%_60%_at_50%_38%,rgba(168,85,247,0.38),transparent_72%)]"
        }`}
        aria-hidden="true"
      />

      {/* Slowly drifting orbs — the depth behind everything else. */}
      <div
        className="mw-aurora absolute -left-24 -top-40 h-[34rem] w-[34rem] rounded-full bg-[#7C3AED]/35 blur-[110px]"
        aria-hidden="true"
      />
      <div
        className="mw-aurora absolute -bottom-52 right-[-6rem] h-[38rem] w-[38rem] rounded-full bg-[#A855F7]/25 blur-[130px]"
        style={{ animationDelay: "-9s" }}
        aria-hidden="true"
      />
      <div
        className="mw-aurora absolute left-1/2 top-1/4 h-[26rem] w-[26rem] rounded-full bg-[#C084FC]/15 blur-[120px]"
        style={{ animationDelay: "-17s" }}
        aria-hidden="true"
      />

      {/* Two shafts of light, out of phase so they never pulse together. */}
      <div
        className="mw-beam absolute -top-1/4 left-[12%] h-[150%] w-[18rem]"
        style={{ "--mw-beam-rot": "14deg" }}
        aria-hidden="true"
      />
      <div
        className="mw-beam absolute -top-1/4 right-[8%] h-[150%] w-[13rem]"
        style={{ "--mw-beam-rot": "-11deg", animationDelay: "-11s" }}
        aria-hidden="true"
      />

      <div className="mw-grid-dark absolute inset-0" aria-hidden="true" />
      <div className="mw-sheen absolute inset-0" aria-hidden="true" />
      <div
        className="mw-grain absolute inset-0 opacity-[0.13] mix-blend-overlay"
        aria-hidden="true"
      />

      {/* Corner vignette — stops the band reading as a flat rectangle. */}
      <div
        className={`absolute inset-0 ${
          isHero
            ? "bg-[radial-gradient(ellipse_88%_72%_at_50%_42%,transparent_42%,rgba(17,2,29,0.6)_100%)]"
            : "bg-[radial-gradient(ellipse_88%_75%_at_50%_45%,transparent_44%,rgba(17,2,29,0.58)_100%)]"
        }`}
        aria-hidden="true"
      />

      {foot && (
        <div
          className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-b from-transparent to-[#1A0229]"
          aria-hidden="true"
        />
      )}
    </>
  );
}

export default HeroBackdrop;
